// Copyright 2026 AllSpice. All rights reserved.

import type {DiffJson} from './diff_json.ts';
import type {TreeNode} from './inspector_tree.ts';

type InspectorTargetDescriptor = {
  elementId: string;
  pageId: string | null;
  pathSegments: string[];
};

function getInspectorTargetDescriptor(targetEl: Element): InspectorTargetDescriptor | null {
  const normalizedTarget = targetEl.closest('[data-id]') ?? targetEl;
  let elementId = normalizedTarget.getAttribute('data-id');
  if (!elementId) {
    return null;
  }

  let pathSegments = normalizedTarget.getAttribute('data-path')?.split('/').filter(Boolean) ?? [];
  if (pathSegments[0] === 'component_instances' && pathSegments.length > 1) {
    elementId = pathSegments[1];
    pathSegments = ['component_instances'];
  }

  return {
    elementId,
    pageId: normalizedTarget.closest('[data-accordion-content]')?.getAttribute('data-accordion-content') ?? null,
    pathSegments,
  };
}

function findNodeFromPath(root: TreeNode, pathSegments: string[], elementId: string): TreeNode | null {
  let currentNode: TreeNode | null = root;
  for (const segment of pathSegments) {
    currentNode = currentNode?.childNode(segment) ?? null;
    if (currentNode === null) {
      return null;
    }
  }

  return currentNode.childNode(elementId);
}

function searchTreeForPath(root: TreeNode, pathSegments: string[], elementId: string): TreeNode | null {
  const stack: TreeNode[] = [root];
  while (stack.length > 0) {
    const currentNode = stack.pop();
    const matchedNode = findNodeFromPath(currentNode, pathSegments, elementId);
    if (matchedNode !== null) {
      return matchedNode;
    }

    for (let index = currentNode.children.length - 1; index >= 0; index -= 1) {
      const child = currentNode.children[index];
      if (child.primitive || child.children.length === 0) {
        continue;
      }

      stack.push(child);
    }
  }

  return null;
}

export type InspectorDiffTreeState = {
  setActiveInspectorDiffTree: (tree: TreeNode | null) => void;
  setRawDiffJson: (diffJson: DiffJson | null) => void;
  clearActiveInspectorDiffTree: () => void;
  getActiveInspectorDiffTree: () => TreeNode | null;
  getRawDiffJson: () => DiffJson | null;
  findPrebuiltInspectorTreeForElement: (targetEl: Element, diffTree?: TreeNode | null) => TreeNode | null;
};

export function createInspectorDiffTreeState(): InspectorDiffTreeState {
  let activeInspectorDiffTree: TreeNode | null = null;
  let rawDiffJson: DiffJson | null = null;

  return {
    setActiveInspectorDiffTree(tree: TreeNode | null): void {
      activeInspectorDiffTree = tree;
    },

    setRawDiffJson(dj: DiffJson | null): void {
      rawDiffJson = dj;
    },

    clearActiveInspectorDiffTree(): void {
      activeInspectorDiffTree = null;
      rawDiffJson = null;
    },

    getActiveInspectorDiffTree(): TreeNode | null {
      return activeInspectorDiffTree;
    },

    getRawDiffJson(): DiffJson | null {
      return rawDiffJson;
    },

    findPrebuiltInspectorTreeForElement(targetEl: Element, diffTree: TreeNode | null = activeInspectorDiffTree): TreeNode | null {
      if (diffTree === null) {
        return null;
      }

      const targetDescriptor = getInspectorTargetDescriptor(targetEl);
      if (targetDescriptor === null) {
        return null;
      }

      const {elementId, pageId, pathSegments} = targetDescriptor;
      const pagesNode = diffTree.childNode('pages');
      if (pageId !== null && pagesNode !== null) {
        const pageNode = pagesNode.childNode(pageId);
        if (pageNode === null) {
          return null;
        }

        return searchTreeForPath(pageNode, pathSegments, elementId);
      }

      return searchTreeForPath(diffTree, pathSegments, elementId);
    },
  };
}
